import apiClient from '../../../../shared/api/apiClient'
import { ENDPOINTS } from '../../../../shared/api/endpoints'
import type {
  LeaveApplication,
  LeaveApplicationsQueryParams,
  LeaveApplicationsResponse,
  ReviewLeaveApplicationPayload,
  StaffLeaveRequest,
  StaffLeaveStatus,
} from '../types/hrmsLeaveRequests.types'

/**
 * Adapter converting a backend LeaveApplication into the StaffLeaveRequest shape
 * expected by UI components.
 * Fields not provided by the API (department/designation) map to "-".
 */
export function mapLeaveApplicationToLeaveRequest(
  app: LeaveApplication
): StaffLeaveRequest {
  const staff = app.staff
  const fullName = staff
    ? `${staff.firstName || ''} ${staff.lastName || ''}`.trim()
    : ''

  const statusMap: Record<string, StaffLeaveStatus> = {
    PENDING: 'Pending',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
    CANCELLED: 'Cancelled',
  }

  const status =
    statusMap[app.status] || (app.status as unknown as StaffLeaveStatus) || 'Pending'

  return {
    id: app.id,
    name: fullName || '-',
    employeeId: staff?.employeeId || '-',
    department: '-', // Field not provided by LeaveApplication API; mapped to "-"
    leaveType: app.leavePolicy?.name || 'Casual Leave',
    from: app.fromDate || '',
    to: app.toDate || '',
    status,
    reason: app.reason || '',
    remarks: app.reviewRemarks || '',
    rejectionReason: app.rejectionReason || '',
    totalDays: app.totalDays,
    isHalfDay: app.isHalfDay,
    halfDaySession: app.halfDaySession,
    attachmentUrl: app.attachmentUrl,
    attachmentName: app.attachmentName,
    reviewedBy: app.reviewedBy,
    reviewerName: app.reviewer
      ? `${app.reviewer.firstName || ''} ${app.reviewer.lastName || ''}`.trim() || null
      : app.reviewedBy || null,
    reviewedAt: app.reviewedAt,
    leavePolicyId: app.leavePolicyId,
    staffId: app.staffId,
    rawApplication: app,
  }
}

function toDateParamString(val: unknown): string | undefined {
  if (val === null || val === undefined) return undefined
  if (val instanceof Date) {
    if (isNaN(val.getTime())) return undefined
    return val.toISOString().split('T')[0]
  }
  if (typeof val === 'string') {
    const trimmed = val.trim()
    if (!trimmed) return undefined
    if (trimmed.includes('T')) {
      const datePart = trimmed.split('T')[0]
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        return datePart
      }
    }
    return trimmed
  }
  if (typeof val === 'number') {
    const d = new Date(val)
    if (!isNaN(d.getTime())) {
      return d.toISOString().split('T')[0]
    }
    return String(val)
  }
  return undefined
}

/**
 * GET LEAVE_APPLICATIONS
 * Query: status, staffId, leavePolicyId, fromDate, toDate, page, limit.
 * Omit empty values.
 */
export async function getLeaveApplications(
  params?: LeaveApplicationsQueryParams
): Promise<LeaveApplicationsResponse> {
  const query: Record<string, string | number> = {}

  if (params?.status) {
    query.status = params.status
  }
  if (params?.staffId && params.staffId.trim()) {
    query.staffId = params.staffId.trim()
  }
  if (params?.leavePolicyId && params.leavePolicyId.trim()) {
    query.leavePolicyId = params.leavePolicyId.trim()
  }
  const fromDateStr = toDateParamString(params?.fromDate)
  if (fromDateStr) {
    query.fromDate = fromDateStr
  }
  const toDateStr = toDateParamString(params?.toDate)
  if (toDateStr) {
    query.toDate = toDateStr
  }
  if (typeof params?.page === 'number') {
    query.page = params.page
  }
  if (typeof params?.limit === 'number') {
    query.limit = params.limit
  }

  const { data } = await apiClient.get<unknown>(ENDPOINTS.HRMS.LEAVE_APPLICATIONS, {
    params: Object.keys(query).length > 0 ? query : undefined,
  })

  const payload = (data as { data?: unknown })?.data ?? data

  const payloadObj = payload as { items?: unknown; total?: unknown; page?: unknown; limit?: unknown }
  const items: LeaveApplication[] = Array.isArray(payload)
    ? (payload as LeaveApplication[])
    : Array.isArray(payloadObj?.items)
      ? (payloadObj.items as LeaveApplication[])
      : []

  const total = typeof payloadObj?.total === 'number' ? payloadObj.total : items.length
  const page = typeof payloadObj?.page === 'number' ? payloadObj.page : (params?.page || 1)
  const limit = typeof payloadObj?.limit === 'number' ? payloadObj.limit : (params?.limit || 20)

  return {
    items,
    total,
    page,
    limit,
  }
}

/**
 * GET LEAVE_APPLICATION_BY_ID(id)
 */
export async function getLeaveApplicationById(id: string): Promise<LeaveApplication> {
  if (!id || !id.trim()) {
    throw new Error('Leave application ID is required')
  }

  const { data } = await apiClient.get<unknown>(
    ENDPOINTS.HRMS.LEAVE_APPLICATION_BY_ID(id.trim())
  )

  const payload = (data as { data?: unknown })?.data ?? data
  return payload as LeaveApplication
}

/**
 * PATCH LEAVE_APPLICATION_REVIEW(id)
 * Body: { status: 'APPROVED' | 'REJECTED', rejectionReason?, reviewRemarks? }
 */
export async function reviewLeaveApplication(
  id: string,
  payload: ReviewLeaveApplicationPayload
): Promise<LeaveApplication> {
  if (!id || !id.trim()) {
    throw new Error('Leave application ID is required')
  }

  const body: Record<string, string> = {
    status: payload.status,
  }

  if (payload.status === 'REJECTED' && payload.rejectionReason?.trim()) {
    body.rejectionReason = payload.rejectionReason.trim()
  }

  if (payload.reviewRemarks && payload.reviewRemarks.trim()) {
    body.reviewRemarks = payload.reviewRemarks.trim()
  }

  const { data } = await apiClient.patch<unknown>(
    ENDPOINTS.HRMS.LEAVE_APPLICATION_REVIEW(id.trim()),
    body
  )

  const res = (data as { data?: unknown })?.data ?? data
  return res as LeaveApplication
}

/**
 * Helper maintaining backward compatibility with existing getLeaveRequests signature.
 */
export async function getLeaveRequests(
  params?: LeaveApplicationsQueryParams
): Promise<StaffLeaveRequest[]> {
  const res = await getLeaveApplications(params)
  return res.items.map(mapLeaveApplicationToLeaveRequest)
}
